import './tutorial.css';
import { t, onLang, hasKey } from './i18n';
import { CHAPTERS, type CourseId, type Mission, type TutorialAction } from '../tutorial/missions';
import { buildGestureBadge, type TutorialGesture } from './tutorialGestures';

/** Which little animated icon illustrates each discrete control action. */
const ACTION_GESTURE: Record<TutorialAction, TutorialGesture> = {
  'aim:enter': { kind: 'click', button: 'left' },
  'aim:exitkey': { kind: 'key', label: '?' },
  'view:orbit': { kind: 'drag', button: 'right' },
  guide: { kind: 'key', label: 'G' },
  chalk: { kind: 'key', label: 'C' },
  'spin:drag': { kind: 'drag', button: 'left' },
  'view:zoom': { kind: 'scroll' },
  'aim:fine': { kind: 'shift-drag' },
  // Never actually drawn — the float-gesture clipart is desktop-only (see
  // render()) and these ids only appear in touchOnly missions. Present anyway
  // so this map stays exhaustive over TutorialAction.
  'touch:aim': { kind: 'move' },
  'touch:orbit': { kind: 'drag', button: 'right' },
  'touch:zoom': { kind: 'scroll' },
};

/**
 * A hero gesture for shot missions that teach one clear physical motion (no
 * discrete `actions` checklist to hang an icon on otherwise). Missions not
 * listed here (bank, carom, …) are "take the shot you already know how to
 * take", so no new gesture needs illustrating.
 */
const MISSION_GESTURE: Partial<Record<string, TutorialGesture>> = {
  stroke: { kind: 'stroke' },
  aim: { kind: 'move' },
  soft: { kind: 'stroke' },
  side: { kind: 'drag', button: 'left' },
  draw: { kind: 'drag', button: 'left' },
  follow: { kind: 'drag', button: 'left' },
};

/**
 * 당구 교실 — the tutorial coach card.
 *
 * A quiet card down the left edge (the one region the HUD leaves empty: score is
 * top-left, status top-centre, the stroke controls bottom-right) that says three
 * things and no more — what chapter you're in, what to do now, and how to do it.
 * It collapses to a single chip, because the one thing a tutorial must never do
 * is stand between the player and the table.
 *
 * The panel is a pure view: it renders a `TutorialView` and reports button
 * presses back. All curriculum state lives in the App.
 */
export interface TutorialPanelCallbacks {
  /** Give up on this mission and move to the next one. */
  onSkip(): void;
  /** Accept the "well done" and start the next mission. */
  onNext(): void;
  /** Put the balls back where the mission started them. */
  onReset(): void;
  /** Leave the tutorial entirely. */
  onExit(): void;
}

export interface TutorialView {
  /** Which of the three courses this session is playing — drives the finish copy. */
  course: CourseId;
  /**
   * The curriculum for this device AND course — the panel counts and draws dots
   * from this, not from the full MISSIONS list, so a touch player isn't shown
   * steps their device never offers, and finishing course 1 doesn't require
   * clearing course 2 and 3's missions too.
   */
  missions: readonly Mission[];
  /** The mission being played, or null when the whole course is finished. */
  mission: Mission | null;
  /** 0-based position of `mission` in the curriculum. */
  index: number;
  /** Which missions are already complete (drives the chapter dots). */
  done: ReadonlySet<string>;
  /**
   * doing  — get on with it
   * passed — just completed; praise up, and the course advances by itself shortly
   * retry  — the last shot didn't do it; showing a gentle nudge
   */
  state: 'doing' | 'passed' | 'retry';
  /** For action missions: which of the required actions have been performed. */
  actionsDone: ReadonlySet<TutorialAction>;
  /** Touch device — swap the keyboard/mouse hint for the gesture one. */
  touch: boolean;
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  return e;
}

/**
 * The how-to line for a mission. Touch devices get `tut.<id>.touch` when the
 * gestures genuinely differ from mouse+keyboard; missions where they don't
 * (press a button, hit a ball) simply omit that key and share the one hint.
 */
function controlHint(mission: Mission, touch: boolean): string {
  const touchKey = `tut.${mission.id}.touch`;
  if (touch && hasKey(touchKey)) return t(touchKey);
  return t(`tut.${mission.id}.keys`);
}

export class TutorialPanel {
  private readonly panel = el('aside', 'hud-panel interactive');
  private readonly chapter = el('div', 'tut-chapter');
  private readonly counter = el('div', 'tut-counter');
  private readonly dots = el('div', 'tut-dots');
  private readonly title = el('div', 'tut-title');
  private readonly goal = el('div', 'tut-goal');
  private readonly keys = el('div', 'tut-keys');
  /** Floats over the CENTRE of the screen, not inside the card — see render(). */
  private readonly floatGesture = el('div', 'tut-float-gesture');
  private readonly checklist = el('div', 'tut-checklist');
  private readonly flash = el('div', 'tut-flash');
  private readonly next = el('button', 'ui-btn ui-btn--go ui-btn--block');
  private readonly reset = el('button', 'ui-btn ui-btn--ghost ui-btn--sm');
  private readonly skip = el('button', 'ui-btn ui-btn--ghost ui-btn--sm');
  private readonly exit = el('button', 'ui-btn ui-btn--ghost ui-btn--sm');
  private readonly body = el('div', 'tut-body');
  private readonly collapseBtn = el('button', 'tut-collapse');
  private collapsed = false;
  private view: TutorialView | null = null;

  constructor(root: HTMLElement, private readonly cb: TutorialPanelCallbacks) {
    this.panel.id = 'tutorial-panel';
    this.panel.style.display = 'none';

    const header = el('div', 'tut-header');
    const heads = el('div', 'tut-heads');
    const brand = el('div', 'tut-brand');
    onLang(() => (brand.textContent = t('tut.title')));
    heads.append(brand, this.chapter);
    this.collapseBtn.onclick = () => this.setCollapsed(!this.collapsed);
    header.append(heads, this.counter, this.collapseBtn);

    const foot = el('div', 'tut-foot');
    this.next.onclick = () => this.cb.onNext();
    this.reset.onclick = () => this.cb.onReset();
    this.skip.onclick = () => this.cb.onSkip();
    this.exit.onclick = () => this.cb.onExit();
    const minor = el('div', 'tut-minor');
    minor.append(this.reset, this.skip, this.exit);
    foot.append(this.next, minor);

    onLang(() => {
      this.reset.textContent = t('tut.reset');
      this.skip.textContent = t('tut.skip');
      this.exit.textContent = t('tut.exit');
      if (this.view) this.render(this.view); // re-render the mission copy too
    });

    this.body.append(this.dots, this.title, this.goal, this.checklist, this.keys, this.flash, foot);
    this.panel.append(header, this.body);
    root.appendChild(this.panel);
    root.appendChild(this.floatGesture);
  }

  get isOpen(): boolean {
    return this.panel.style.display !== 'none';
  }

  show(): void {
    this.panel.style.display = '';
  }

  hide(): void {
    this.panel.style.display = 'none';
    this.floatGesture.replaceChildren();
  }

  private setCollapsed(v: boolean): void {
    this.collapsed = v;
    this.body.style.display = v ? 'none' : '';
    this.panel.classList.toggle('is-collapsed', v);
    this.collapseBtn.textContent = v ? '▸' : '▾';
    this.collapseBtn.title = t(v ? 'tut.expand' : 'tut.collapse');
  }

  /** Render a view. Safe to call every time anything changes. */
  render(view: TutorialView): void {
    this.view = view;
    this.setCollapsed(this.collapsed);

    // ---- course complete ------------------------------------------------
    if (!view.mission) {
      this.chapter.textContent = '';
      this.counter.textContent = `${view.missions.length} / ${view.missions.length}`;
      this.renderDots(view.missions, view.done, null);
      this.title.textContent = t(`tut.done.${view.course}.title`);
      // The control course's desktop summary talks about aim mode, which touch
      // doesn't have — swap in a touch-worded goal where one exists.
      const goalKey = `tut.done.${view.course}.goal`;
      const touchGoalKey = `${goalKey}.touch`;
      this.goal.textContent = view.touch && hasKey(touchGoalKey) ? t(touchGoalKey) : t(goalKey);
      this.floatGesture.replaceChildren();
      this.keys.textContent = '';
      this.checklist.replaceChildren();
      this.flash.textContent = '';
      this.flash.className = 'tut-flash';
      this.next.textContent = t('tut.finish');
      this.next.style.display = '';
      this.next.onclick = () => this.cb.onExit();
      this.reset.style.display = 'none';
      this.skip.style.display = 'none';
      return;
    }

    const m = view.mission;
    this.next.onclick = () => this.cb.onNext();
    this.reset.style.display = '';
    this.skip.style.display = '';
    this.chapter.textContent = t(`tut.chapter.${m.chapter}`);
    this.counter.textContent = `${view.index + 1} / ${view.missions.length}`;
    this.renderDots(view.missions, view.done, m);
    this.title.textContent = t(`tut.${m.id}.title`);
    this.goal.textContent = t(`tut.${m.id}.goal`);
    this.keys.textContent = controlHint(m, view.touch);

    // Action missions show their required controls as a tick list, so a mission
    // asking for two things doesn't look stuck after you've done one of them.
    this.checklist.replaceChildren();
    if (m.actions) {
      for (const a of m.actions) {
        const row = el('div', 'tut-check');
        row.classList.toggle('is-done', view.actionsDone.has(a));
        const box = el('span', 'tut-check__box');
        box.textContent = view.actionsDone.has(a) ? '✓' : '○';
        const label = el('span');
        label.textContent = t(`tut.action.${a}`);
        row.append(box, label);
        this.checklist.appendChild(row);
      }
    }

    // The gesture clipart floats over the CENTRE of the table, not the card —
    // it's illustrating a physical motion the player performs out there, not
    // reading material. Action missions show the next unfinished control;
    // shot missions show their one hero gesture, when they have one.
    this.floatGesture.replaceChildren();
    if (!view.touch && view.state !== 'passed') {
      const g = m.actions
        ? ACTION_GESTURE[m.actions.find((a) => !view.actionsDone.has(a)) ?? m.actions[0]]
        : MISSION_GESTURE[m.id];
      if (g) this.floatGesture.appendChild(buildGestureBadge(g));
    }

    // ---- state banner ----------------------------------------------------
    this.flash.className = 'tut-flash';
    if (view.state === 'passed') {
      this.flash.classList.add('is-good');
      // The course advances on its own — chime + auto-advance — so there is no
      // "Next step" button to press here; pressing ahead would just race the timer.
      this.flash.textContent = t('tut.passedNext');
      this.next.style.display = 'none';
    } else if (view.state === 'retry') {
      this.flash.classList.add('is-retry');
      this.flash.textContent = t(`tut.${m.id}.retry`);
      this.next.style.display = 'none';
    } else {
      this.flash.textContent = '';
      this.next.style.display = 'none';
    }
  }

  /**
   * One dot per mission, grouped by chapter: filled = done, ringed = the one
   * you're on. It's the "how much is left" answer without a wall of text.
   */
  private renderDots(missions: readonly Mission[], done: ReadonlySet<string>, current: Mission | null): void {
    this.dots.replaceChildren();
    for (const ch of CHAPTERS) {
      const inCourse = missions.filter((x) => x.chapter === ch);
      if (inCourse.length === 0) continue; // chapter belongs to a different course
      const group = el('div', 'tut-dotgroup');
      for (const m of inCourse) {
        const d = el('span', 'tut-dot');
        if (done.has(m.id)) d.classList.add('is-done');
        if (current && m.id === current.id) d.classList.add('is-now');
        d.title = t(`tut.${m.id}.title`);
        group.appendChild(d);
      }
      this.dots.appendChild(group);
    }
  }
}
